import { ErrorHandler, Injectable, inject } from '@angular/core';
import { Router } from '@angular/router';
import { Auth } from 'firebase/auth';
import { FIREBASE_AUTH } from '../firebase-config';
import { AuthenticationService } from './authentication.service';
import { GlobalService } from './global.service';

@Injectable()
export class GlobalErrorHandler implements ErrorHandler {
  private router = inject(Router);
  private auth = inject(FIREBASE_AUTH) as Auth;
  private authService = inject(AuthenticationService);
  private globalService = inject(GlobalService);

  handleError(error: unknown): void {
    const firebaseError = this.extractFirebaseError(error);

    if (firebaseError) {
      const code = firebaseError.code;
      const isAuthMissing = !this.auth.currentUser;
      const url = this.router.url ?? '';
      const isPublicRoute = url === '/public' || url.startsWith('/public/');
      const isLogoutNoise = this.authService.logoutInProgress || (isAuthMissing && isPublicRoute);

      // Firestore listeners can legitimately emit permission errors right after sign-out.
      // Treat these as expected noise during logout / when unauthenticated.
      if (isLogoutNoise && (code === 'permission-denied' || code === 'unauthenticated')) {
        return;
      }

      // Otherwise: keep logging, to surface real permission issues.
    }

    const candidate = this.unwrapError(error);
    const message = this.getFieldAsString(candidate, 'message') ?? String(error);
    const stack = this.getFieldAsString(candidate, 'stack');

    console.error('🔴 Global Error Handler caught:', error);
    console.error('Error message:', message);
    if (stack) {
      console.error('Error stack:', stack);
    }

    // Surface unexpected runtime errors to users as dismissable flash messages.
    void this.presentRuntimeErrorToast(message);
  }

  private async presentRuntimeErrorToast(message: string): Promise<void> {
    try {
      const lowerMessage = message.toLowerCase();
      const isExpressionChangedError =
        lowerMessage.includes('expressionchangedafterithasbeencheckederror') ||
        lowerMessage.includes('ng0100');

      if (isExpressionChangedError) {
        await this.globalService.presentErrorToast('public.login.fail.auth/internal-error');
        return;
      }

      await this.globalService.presentErrorToast(message);
    } catch {
      // Avoid cascading errors from the global error handler itself.
    }
  }

  private extractFirebaseError(error: unknown): { code?: string; message?: string } | null {
    const candidate = this.unwrapError(error);
    if (!candidate || typeof candidate !== 'object') {
      return null;
    }

    // FirebaseError typically has: { name: 'FirebaseError', code: 'permission-denied', message: '...' }
    const code = this.getFieldAsString(candidate, 'code');
    const name = this.getFieldAsString(candidate, 'name');
    const message = this.getFieldAsString(candidate, 'message');

    if (code && (name === 'FirebaseError' || name === 'FirebaseError:')) {
      return { code, message: message ?? undefined };
    }

    // Fallback: code present but name differs (defensive)
    if (code && message) {
      return { code, message };
    }

    return null;
  }

  private unwrapError(error: unknown): unknown {
    if (typeof error !== 'object' || error === null) {
      return error;
    }

    const rejection = Reflect.get(error, 'rejection');
    return rejection ?? error;
  }

  private getFieldAsString(value: unknown, field: string): string | null {
    if (typeof value !== 'object' || value === null) {
      return null;
    }

    const fieldValue = Reflect.get(value, field);
    return typeof fieldValue === 'string' ? fieldValue : null;
  }
}
